import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { toast } from 'sonner';
import Header from '../../components/ui/Header';
import BackButton from '../../components/ui/BackButton';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { nearMissService } from '../../services/nearMissService';
import { companyService } from '../../services/companyService';
import { siteService } from '../../services/siteService';
import { pdfExportService } from '../../services/pdfExportService';
import { sanitizeError } from '../../utils/sanitizeError';
import ReportNearMissModal from './components/ReportNearMissModal';
import NearMissDetailModal from './components/NearMissDetailModal';
import NearMissSummaryCards from './components/NearMissSummaryCards';
import NearMissFilters from './components/NearMissFilters';
import NearMissTable from './components/NearMissTable';
import NearMissDeleteConfirmModal from './components/NearMissDeleteConfirmModal';

const NearMissesPage = () => {
  const { userProfile } = useAuth();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();

  // Role-based BackButton fallback for the rare direct-URL case where history
  // is empty — mirrors the pattern used on the sibling incident-reporting
  // page so admins and site_managers land on the right dashboard.
  const homePath =
    userProfile?.role === 'worker' ? '/worker-today'
    : userProfile?.role === 'site_manager' ? '/site-manager-dashboard'
    : '/admin-dashboard';

  const [nearMisses, setNearMisses] = useState([]);
  const [sites, setSites] = useState([]);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedNearMiss, setSelectedNearMiss] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // NSherjFh PR 5: tab between active and soft-deleted near-misses;
  // confirm modal replaces the prior window.confirm() hard-delete prompt.
  const isAdmin = userProfile?.role === 'admin';
  const [viewDeleted, setViewDeleted] = useState(false);
  const [deletingNearMiss, setDeletingNearMiss] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Active card filter (status key or 'total')
  const [cardFilter, setCardFilter] = useState(null);

  const [filters, setFilters] = useState({
    search: '',
    site_id: '',
    risk_level: 'All',
    status: searchParams?.get('status') === 'open' ? 'Open' : 'All',
  });

  useEffect(() => {
    loadData();
  }, [viewDeleted]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [companyResult, sitesResult] = await Promise.all([
        companyService?.getUserCompany(),
        siteService?.getAllSites(),
      ]);

      const comp = companyResult?.data;
      setCompany(comp);
      setSites(sitesResult?.data || []);

      if (comp?.id) {
        const { data, error: nmError } = await nearMissService?.getNearMissesForCompany(
          comp?.id,
          {},
          { includeDeleted: viewDeleted }
        );
        if (nmError) throw new Error(nmError);
        // NSherjFh PR 5: deleted tab shows ONLY soft-deleted near-misses.
        const rows = data || [];
        setNearMisses(viewDeleted ? rows?.filter((nm) => nm?.deleted_at) : rows);
      }
    } catch (err) {
      setError(sanitizeError(err?.message, 'Failed to load near misses'));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    if (key === 'status') setCardFilter(null);
  };

  const handleClearFilters = () => {
    setFilters({ search: '', site_id: '', risk_level: 'All', status: 'All' });
    setCardFilter(null);
  };

  const handleCardFilter = (key) => {
    setCardFilter(key);
    if (key === 'total' || key === null) {
      setFilters(prev => ({ ...prev, status: 'All' }));
    } else {
      setFilters(prev => ({ ...prev, status: key }));
    }
  };

  const handleViewDetail = (nm) => {
    setSelectedNearMiss(nm);
    setShowDetailModal(true);
  };

  const handleStatusUpdated = (updated) => {
    setNearMisses(prev => prev?.map(nm => nm?.id === updated?.id ? { ...nm, status: updated?.status } : nm));
    setSelectedNearMiss(prev => prev?.id === updated?.id ? { ...prev, status: updated?.status } : prev);
  };

  // NSherjFh PR 5 — soft-delete handlers. Replaces the prior
  // window.confirm() + hard-DELETE flow with modal + idempotent
  // soft-delete UPDATE. The mobile-card and desktop table both
  // route through handleDeleteClick now.
  const handleDeleteClick = (nm) => {
    setDeletingNearMiss(nm);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingNearMiss?.id) {
      setIsDeleteModalOpen(false);
      return;
    }
    const target = deletingNearMiss;
    try {
      const { error } = await nearMissService?.deleteNearMiss(target?.id);
      if (error) throw new Error(error);
      setNearMisses((prev) => prev?.filter((r) => r?.id !== target?.id));
      toast?.success('Near miss moved to Deleted');
    } catch (err) {
      toast?.error(err?.message || 'Failed to delete near miss');
    } finally {
      setIsDeleteModalOpen(false);
      setDeletingNearMiss(null);
    }
  };

  const handleRestore = async (nm) => {
    try {
      const { error } = await nearMissService?.restoreNearMiss(nm?.id);
      if (error) throw new Error(error);
      setNearMisses((prev) => prev?.filter((r) => r?.id !== nm?.id));
      toast?.success('Near miss restored');
    } catch (err) {
      toast?.error(err?.message || 'Failed to restore near miss');
    }
  };

  // Compute stats
  const stats = useMemo(() => ({
    total: nearMisses?.length,
    open: nearMisses?.filter(nm => nm?.status === 'Open')?.length,
    underReview: nearMisses?.filter(nm => nm?.status === 'Under review')?.length,
    closed: nearMisses?.filter(nm => nm?.status === 'Closed')?.length,
  }), [nearMisses]);

  // Apply filters
  const filteredNearMisses = useMemo(() => {
    return nearMisses?.filter(nm => {
      if (filters?.site_id && nm?.site_id !== filters?.site_id) return false;
      if (filters?.risk_level !== 'All' && nm?.risk_level !== filters?.risk_level) return false;
      if (filters?.status !== 'All' && nm?.status !== filters?.status) return false;
      if (filters?.search) {
        const q = filters?.search?.toLowerCase();
        const matchDesc = nm?.description?.toLowerCase()?.includes(q);
        const matchLoc = nm?.location?.toLowerCase()?.includes(q);
        if (!matchDesc && !matchLoc) return false;
      }
      return true;
    });
  }, [nearMisses, filters]);

  const handleExportPDF = async () => {
    if (!filteredNearMisses?.length) {
      toast?.error('No records to export');
      return;
    }
    setExporting(true);
    try {
      const selectedSite = sites?.find(s => s?.id === filters?.site_id);
      await pdfExportService?.generateNearMissReport({
        nearMisses: filteredNearMisses,
        siteName: selectedSite?.site_name || 'All Sites',
        companyName: company?.company_name || 'SiteProof',
        dateFrom: filters?.date_from || null,
        dateTo: filters?.date_to || null,
      });
      toast?.success('PDF exported successfully');
    } catch (err) {
      toast?.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const getRiskBadgeClass = (level) => {
    if (level === 'High') return 'bg-red-100 text-red-700';
    if (level === 'Medium') return 'bg-amber-100 text-amber-700';
    return 'bg-emerald-100 text-emerald-700';
  };

  const getStatusBadgeClass = (status) => {
    if (status === 'Closed') return 'bg-emerald-100 text-emerald-700';
    if (status === 'Under review') return 'bg-blue-100 text-blue-700';
    return 'bg-amber-100 text-amber-700';
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Near Misses | SiteProof</title>
      </Helmet>

      <Header />

      <main className="container mx-auto px-3 sm:px-4 pt-24 pb-8 max-w-7xl">
        {/* Back navigation — matches incident-reporting / worker-certifications pattern */}
        <div className="mb-4 flex items-center gap-4">
          <BackButton fallback={homePath} />
        </div>

        {/* Page Header */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground font-heading mb-0.5 truncate">
              {viewDeleted ? 'Deleted near misses' : t('nearMissesTitle')}
            </h1>
            <p className="text-sm text-muted-foreground hidden sm:block">
              {viewDeleted
                ? 'Near miss reports removed from active views. Restore to bring them back.'
                : t('nearMissesSubtitle')}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* NSherjFh PR 5: admin-only View Deleted toggle. */}
            {isAdmin && (
              <Button
                variant={viewDeleted ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewDeleted((v) => !v)}
                className="flex items-center gap-1.5"
              >
                <Icon name={viewDeleted ? 'FolderOpen' : 'Trash2'} size={15} />
                <span className="hidden sm:inline">{viewDeleted ? 'View active' : 'View deleted'}</span>
              </Button>
            )}
            {!viewDeleted && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPDF}
                disabled={exporting || !filteredNearMisses?.length}
                className="flex items-center gap-1.5"
              >
                {exporting ? (
                  <Icon name="Loader2" size={15} className="animate-spin" />
                ) : (
                  <Icon name="Download" size={15} />
                )}
                <span className="hidden sm:inline">{t('exportPdf')}</span>
              </Button>
            )}
            {!viewDeleted && (
              <Button
                variant="default"
                size="sm"
                onClick={() => setShowReportModal(true)}
                className="flex items-center gap-1.5"
              >
                <Icon name="Plus" size={15} />
                <span className="hidden sm:inline">{t('reportNearMiss')}</span>
                <span className="sm:hidden">Report</span>
              </Button>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-6 flex items-center gap-2 text-destructive">
            <Icon name="AlertCircle" size={18} />
            <p className="text-sm font-medium">{error}</p>
          </div>
        )}

        {/* NSherjFh PR 5: stats + filters are meaningless on the
            deleted tab. Hide them when viewing soft-deleted reports. */}
        {!viewDeleted && (
          <NearMissSummaryCards
            stats={stats}
            onFilter={handleCardFilter}
            activeFilter={cardFilter}
          />
        )}

        {/* Filters + Table */}
        <div className="bg-card rounded-xl border border-border shadow-elevation-1 p-4 sm:p-5">
          {!viewDeleted && (
            <div className="flex flex-col gap-3 mb-4">
              <NearMissFilters
                filters={filters}
                sites={sites}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
              />
            </div>
          )}

          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-muted-foreground">
              {filteredNearMisses?.length} {t('nearMissRecordsFound')}{filteredNearMisses?.length !== 1 ? 's' : ''} {t('nearMissFound')}
            </p>
          </div>

          {/* Desktop table */}
          <div className="hidden md:block">
            <NearMissTable
              nearMisses={filteredNearMisses}
              loading={loading}
              onViewDetail={handleViewDetail}
              onDelete={!viewDeleted && isAdmin ? handleDeleteClick : null}
              onRestore={viewDeleted && isAdmin ? handleRestore : null}
            />
          </div>

          {/* Mobile card-stack */}
          <div className="md:hidden">
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent" />
              </div>
            ) : filteredNearMisses?.length === 0 ? (
              <div className="text-center py-10">
                <Icon name="AlertTriangle" size={32} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-sm font-medium text-foreground">No near misses found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredNearMisses?.map(nm => (
                  <div
                    key={nm?.id}
                    className="border border-border rounded-xl p-4 bg-background"
                    onClick={() => handleViewDetail(nm)}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <p className="text-sm font-semibold text-foreground leading-snug line-clamp-2 flex-1">
                        {nm?.description || 'No description'}
                      </p>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${getRiskBadgeClass(nm?.risk_level)}`}>
                        {nm?.risk_level}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                      {nm?.location && (
                        <span className="flex items-center gap-1">
                          <Icon name="MapPin" size={11} />
                          {nm?.location}
                        </span>
                      )}
                      {nm?.date && (
                        <span className="flex items-center gap-1">
                          <Icon name="Calendar" size={11} />
                          {new Date(nm?.date)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${getStatusBadgeClass(nm?.status)}`}>
                        {nm?.status}
                      </span>
                      {/* NSherjFh PR 5: mobile card action mirrors desktop —
                          Delete (active view) or Restore (deleted view),
                          admin-only. */}
                      {!viewDeleted && isAdmin && (
                        <button
                          onClick={e => { e?.stopPropagation(); handleDeleteClick(nm); }}
                          className="p-2 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                          aria-label="Delete"
                        >
                          <Icon name="Trash2" size={15} />
                        </button>
                      )}
                      {viewDeleted && isAdmin && (
                        <button
                          onClick={e => { e?.stopPropagation(); handleRestore(nm); }}
                          className="p-2 rounded-lg hover:bg-accent/10 text-muted-foreground hover:text-accent transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                          aria-label="Restore"
                        >
                          <Icon name="RotateCcw" size={15} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Report Modal */}
      <ReportNearMissModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        onSuccess={() => { loadData(); setShowReportModal(false); }}
      />

      {selectedNearMiss && (
        <NearMissDetailModal
          isOpen={showDetailModal}
          nearMiss={selectedNearMiss}
          onClose={() => { setShowDetailModal(false); setSelectedNearMiss(null); }}
          onStatusUpdated={handleStatusUpdated}
        />
      )}

      <NearMissDeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setDeletingNearMiss(null); }}
        onConfirm={handleConfirmDelete}
        nearMissLabel={deletingNearMiss?.description?.slice(0, 80) || deletingNearMiss?.location || ''}
      />
    </div>
  );
};

export default NearMissesPage;
